import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule, MatDrawer } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FancySpinnerComponent } from './shared/components/fancy-spinner/fancy-spinner.component';
import { FancyButtonComponent } from './shared/components/fancy-button/fancy-button.component';
import { FancyChipComponent } from './shared/components/fancy-chip/fancy-chip.component';
import { FancyInputComponent } from './shared/components/fancy-input/fancy-input.component';
import { FancyAutocompleteComponent } from './shared/components/fancy-autocomplete/fancy-autocomplete.component';
import {
  FancyButtonToggleComponent,
  FancyToggleOption,
} from './shared/components/fancy-button-toggle/fancy-button-toggle.component';
import { ClientSelectorPanelComponent } from './shared/components/client-selector-panel/client-selector-panel.component';
import { AuthService } from './core/services/auth.service';
import { CartService } from './core/services/cart.service';
import { PosToolbarService } from './features/sales/pos/services/pos-toolbar.service';
import { PermissionService, PermissionKey } from './core/services/permission.service';
import { Client } from './core/models/client.model';
import { filter } from 'rxjs';
import { ViewportService } from './core/services/viewport.service';
import { TransferNotificationsComponent } from './shared/components/transfer-notifications/transfer-notifications.component';
import { PastDueBannerComponent } from './shared/components/past-due-banner/past-due-banner.component';
import { BusinessContextService } from './core/services/multi-tenant/business-context.service';
import { PlanCatalogService } from './core/services/plan-catalog.service';
import { getRoleLabel } from './core/models/employee.model';
import { Subscription } from 'rxjs';
import type { PlanId } from './core/models/plan.model';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatBadgeModule,
    MatDividerModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatTooltipModule,
    FancySpinnerComponent,
    FancyButtonComponent,
    FancyChipComponent,
    FancyInputComponent,
    FancyAutocompleteComponent,
    FancyButtonToggleComponent,
    ClientSelectorPanelComponent,
    TransferNotificationsComponent,
    PastDueBannerComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private cartService = inject(CartService);
  private permissionService = inject(PermissionService);
  private businessContextService = inject(BusinessContextService);
  private planCatalog = inject(PlanCatalogService);
  posToolbarService = inject(PosToolbarService);
  viewport = inject(ViewportService);
  private router = inject(Router);
  private suspensionSub?: Subscription;

  // Signals
  currentUser = computed(() => this.authService.currentUser());
  platformAdmin = computed(() => this.authService.platformAdmin());
  businessContext = computed(() => this.authService.currentBusinessContext());
  userRole = computed(() => this.authService.userRole());
  authLoading = computed(() => this.authService.isLoading());
  // Aliased from ViewportService for backwards compatibility with the template.
  readonly isHandset = this.viewport.isMobile;
  readonly isDesktopWide = this.viewport.isDesktop;
  isLoginPage = signal(false);
  clientPanelOpen = signal(false);

  navigationItems: {
    icon: string;
    label: string;
    route: string;
    permission?: PermissionKey;
    // If set, this item is only visible when the module id is in the business's
    // effective module list (the plan's `modulosHabilitados`). No moduleId = always
    // visible (core items: Dashboard, Facturar, Ventas, Clientes, Configuración).
    moduleId?: string;
    expanded?: boolean;
    children?: {
      icon: string;
      label: string;
      route: string;
      permission?: PermissionKey;
      moduleId?: string;
    }[];
  }[] = [
    {
      icon: 'dashboard',
      label: 'Dashboard',
      route: '/dashboard',
      permission: 'canAccessDashboard',
    },
    {
      icon: 'receipt_long',
      label: 'Facturar',
      route: '/facturar',
      permission: 'canCreateSale',
    },
    {
      icon: 'point_of_sale',
      label: 'Vender',
      moduleId: 'pos',
      route: '/pos',
      permission: 'canAccessPOS',
      expanded: true,
      children: [
        {
          icon: 'shopping_cart',
          label: '¡Vender!',
          route: '/pos',
          permission: 'canCreateSale',
          moduleId: 'pos',
        },
        {
          icon: 'badge',
          label: 'Cajeros',
          route: '/cash-registers',
          permission: 'canAccessCashRegisters',
          moduleId: 'pos',
        },
        {
          icon: 'payment',
          label: 'Métodos de Pago',
          route: '/payment-methods',
          permission: 'canManagePaymentMethods',
          moduleId: 'pos',
        },
        {
          icon: 'settings',
          label: 'Configuración de POS',
          route: '/pos-settings',
          permission: 'canAccessPOSSettings',
          moduleId: 'pos',
        },
      ],
    },
    {
      icon: 'inventory',
      label: 'Productos',
      route: '/products',
      permission: 'canAccessProducts',
      moduleId: 'inventory',
      expanded: true,
      children: [
        {
          icon: 'list',
          label: 'Ver todos',
          route: '/products',
          permission: 'canAccessProducts',
          moduleId: 'inventory',
        },
        {
          icon: 'category',
          label: 'Categorías',
          route: '/products/categories',
          permission: 'canManageCategories',
          moduleId: 'inventory',
        },
        {
          icon: 'tune',
          label: 'Variantes',
          route: '/products/variants',
          permission: 'canManageVariants',
          moduleId: 'inventory',
        },
      ],
    },
    {
      icon: 'warehouse',
      label: 'Ubicaciones',
      route: '/locations',
      permission: 'canAccessLocations',
      moduleId: 'inventory',
    },
    {
      icon: 'inventory_2',
      label: 'Inventario',
      route: '/inventory/movements',
      permission: 'canAccessInventory',
      moduleId: 'inventory',
      expanded: true,
      children: [
        {
          icon: 'list',
          label: 'Ver todos',
          route: '/inventory/movements',
          permission: 'canViewMovementHistory',
          moduleId: 'inventory',
        },
        {
          icon: 'add_shopping_cart',
          label: 'Reabastecimiento',
          route: '/inventory/restocking',
          permission: 'canReceiveStock',
          moduleId: 'inventory',
        },
        {
          icon: 'assignment',
          label: 'Conteo de Inventario',
          route: '/inventory/stock-take',
          permission: 'canPerformStockTake',
          moduleId: 'inventory',
        },
        {
          icon: 'sync_alt',
          label: 'Transferencias',
          route: '/inventory/transfers',
          permission: 'canTransferStock',
          moduleId: 'inventory',
        },
      ],
    },
    {
      icon: 'shopping_cart',
      label: 'Compras',
      route: '/purchases',
      permission: 'canAccessPurchases',
      moduleId: 'purchasing',
      expanded: true,
      children: [
        {
          icon: 'list',
          label: 'Ver todas',
          route: '/purchases',
          permission: 'canViewPurchaseOrders',
          moduleId: 'purchasing',
        },
        {
          icon: 'local_shipping',
          label: 'Proveedores',
          route: '/purchases/suppliers',
          permission: 'canManageSuppliers',
          moduleId: 'purchasing',
        },
        {
          icon: 'account_balance_wallet',
          label: 'Cuentas por Pagar',
          route: '/purchases/accounts-payable',
          permission: 'canViewAccountsPayable',
          moduleId: 'purchasing',
        },
      ],
    },
    {
      icon: 'payments',
      label: 'Gastos',
      route: '/expenses',
      permission: 'canAccessExpenses',
      moduleId: 'expenses',
      expanded: true,
      children: [
        {
          icon: 'list',
          label: 'Todos los gastos',
          route: '/expenses',
          permission: 'canViewExpenses',
          moduleId: 'expenses',
        },
        {
          icon: 'category',
          label: 'Categorías',
          route: '/expenses/categories',
          permission: 'canManageExpenseCategories',
          moduleId: 'expenses',
        },
        {
          icon: 'request_quote',
          label: 'Ingresos no operacionales',
          route: '/expenses/non-operational-income',
          permission: 'canViewExpenses',
          moduleId: 'expenses',
        },
      ],
    },
    {
      icon: 'receipt_long',
      label: 'Ventas',
      route: '/sales',
      permission: 'canAccessSales',
      expanded: true,
      children: [
        {
          icon: 'list',
          label: 'Ver todas',
          route: '/sales',
          permission: 'canAccessSales',
        },
        {
          icon: 'credit_score',
          label: 'Cuentas por Cobrar',
          route: '/sales/accounts-receivable',
          permission: 'canViewAccountsReceivable',
          // Ungated: accounts-receivable is tied to sales/invoicing (core to every
          // plan); there is no dedicated module id for it in app.routes.ts.
        },
        {
          icon: 'sell',
          label: 'Listas de Precios',
          route: '/sales/price-lists',
          permission: 'canManagePriceLists',
          // Never hidden for lack of lists — this is where the first one is created.
        },
      ],
    },
    {
      icon: 'people',
      label: 'Clientes',
      route: '/clients',
      permission: 'canAccessCustomers',
    },
    {
      icon: 'badge',
      label: 'Empleados',
      route: '/employees',
      permission: 'canAccessEmployees',
      moduleId: 'employees',
      expanded: true,
      children: [
        {
          icon: 'list',
          label: 'Ver todos',
          route: '/employees',
          permission: 'canViewEmployees',
          moduleId: 'employees',
        },
        {
          icon: 'account_balance_wallet',
          label: 'Salarios',
          route: '/employees/salaries',
          permission: 'canViewSalaries',
          moduleId: 'employees',
        },
      ],
    },
    {
      icon: 'analytics',
      label: 'Reportes',
      route: '/reports',
      permission: 'canAccessReports',
      moduleId: 'reports',
    },
    {
      icon: 'settings',
      label: 'Configuración',
      route: '/settings',
      permission: 'canAccessSettings',
      expanded: true,
      children: [
        {
          icon: 'store',
          label: 'Perfil del Negocio',
          route: '/settings/business-profile',
          permission: 'canEditBusinessSettings',
        },
        {
          icon: 'credit_card',
          label: 'Suscripción',
          route: '/settings/subscription',
          permission: 'canManageSubscription',
        },
        {
          icon: 'receipt_long',
          label: 'Facturación Electrónica',
          route: '/settings/electronic-invoicing',
          permission: 'canEditBusinessSettings',
        },
        {
          icon: 'shopping_cart',
          label: 'Compras',
          route: '/settings/purchases',
          permission: 'canEditBusinessSettings',
          moduleId: 'purchasing',
        },
      ],
    },
  ];

  // View mode toggle options for POS toolbar
  viewModeOptions: FancyToggleOption[] = [
    { value: 'carousel', label: 'Carrusel', icon: 'view_carousel' },
    { value: 'simple', label: 'Lista', icon: 'view_list' },
    { value: 'grid', label: 'Cuadrícula', icon: 'grid_view' },
  ];

  ngOnInit() {
    // Monitor route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isLoginPage.set(event.url === '/login' || event.url.startsWith('/register'));
        // Update POS route status
        this.posToolbarService.isPosRoute.set(
          event.url.startsWith('/pos') && !event.url.startsWith('/pos-settings')
        );
      });

    // Check initial route
    this.isLoginPage.set(this.router.url === '/login' || this.router.url.startsWith('/register'));
    this.posToolbarService.isPosRoute.set(
      this.router.url.startsWith('/pos') && !this.router.url.startsWith('/pos-settings')
    );

    // Real-time suspension watcher — redirect mid-session if subscription gets suspended
    this.suspensionSub = this.businessContextService.currentBusiness$.subscribe(business => {
      if (!business?.subscription?.status) return;
      // Only act for business users, not platform admins
      if (this.platformAdmin()) return;
      const status = business.subscription.status;
      if (status === 'suspended') {
        // Avoid redirect loop if already on suspended/settings page
        const url = this.router.url;
        if (
          !url.startsWith('/suspended') &&
          !url.startsWith('/settings') &&
          !url.startsWith('/login')
        ) {
          this.router.navigate(['/suspended']);
        }
      }

      if (status === 'cancelled') {
        const now = new Date();
        const sub = business.subscription;
        const trialEnd = sub.trialEndsAt ? toDate(sub.trialEndsAt) : null;
        const billingEnd = sub.nextBillingDate ? toDate(sub.nextBillingDate) : null;
        const gracePeriodEnd = trialEnd || billingEnd;

        // Only redirect if grace period has expired
        if (!gracePeriodEnd || now >= gracePeriodEnd) {
          const url = this.router.url;
          if (
            !url.startsWith('/suspended') &&
            !url.startsWith('/settings') &&
            !url.startsWith('/login')
          ) {
            this.router.navigate(['/suspended']);
          }
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.suspensionSub?.unsubscribe();
  }

  getFilteredNavItems = computed(() => {
    const isPlatformAdmin = this.platformAdmin();
    const currentUser = this.currentUser();

    if (isPlatformAdmin) {
      // Platform admins have different navigation
      return [
        {
          icon: 'admin_panel_settings',
          label: 'Platform Admin',
          route: '/platform-admin',
          expanded: false,
          children: [] as {
            icon: string;
            label: string;
            route: string;
            permission?: PermissionKey;
            moduleId?: string;
          }[],
        },
      ];
    }

    if (!currentUser) return [];

    // --- Effective module list (S4-T6) -------------------------------------
    // Gate nav items by the plan's enabled MODULES (`modulosHabilitados`), NOT by
    // legacy planId names. Source of the subscription:
    //   1. `businessContextService.currentBusiness()` — the live, refreshable
    //      business doc (re-emits on `refreshCurrentBusiness`, e.g. after a plan
    //      change), so the sidebar reacts to plan changes without a full reload.
    //   2. fallback: the auth `businessContext()` set at login (initial frame).
    const liveBusiness = this.businessContextService.currentBusiness();
    const context = this.businessContext();
    const subscription = liveBusiness?.subscription ?? context?.business?.subscription;
    const planId = (subscription?.planId as PlanId | undefined) ?? undefined;

    // For CUSTOM plans, use the per-business override module list. For PUBLIC
    // plans, read the authoritative module list from the catalog (robust even if
    // a migrated business doc never had `modulosHabilitados` written).
    const planDoc = planId ? this.planCatalog.planById(planId) : undefined;
    const effectiveModules: string[] =
      planId === 'custom'
        ? (subscription?.modulosHabilitados ?? [])
        : (planDoc?.modulosHabilitados ?? []);

    // Load-flicker handling: while the plan/catalog is still resolving we cannot
    // tell which modules are enabled. Hiding everything would flash an empty
    // sidebar. So if we can't resolve a module list yet (no planId, or catalog
    // not loaded for a public plan), DON'T hide gated items — show them until the
    // plan resolves, then apply the real filter.
    const planResolved =
      planId === 'custom' ? subscription?.modulosHabilitados !== undefined : planDoc !== undefined;

    const moduleVisible = (item: { moduleId?: string }) =>
      !item.moduleId || !planResolved || effectiveModules.includes(item.moduleId);

    // Filter navigation items based on permissions and enabled modules
    return this.navigationItems
      .filter(item => moduleVisible(item))
      .filter(item => !item.permission || this.permissionService.hasPermission(item.permission))
      .map(item => ({
        ...item,
        children:
          item.children?.filter(
            child =>
              moduleVisible(child) &&
              (!child.permission || this.permissionService.hasPermission(child.permission))
          ) || [],
      }))
      .filter(item => item.children.length > 0 || !item.expanded);
  });

  async logout() {
    await this.authService.logout();
  }

  closeDrawerIfHandset(drawer: MatDrawer): void {
    if (this.isHandset()) {
      drawer.close();
    }
  }

  toggleSubmenu(item: any): void {
    item.expanded = !item.expanded;
  }

  displayClientName(client: any): string {
    return client ? client.name : '';
  }

  truncateClientName(name: string): string {
    const words = name.trim().split(/\s+/);
    if (words.length <= 2) return name.trim();
    return words.slice(0, 2).join(' ') + '...';
  }

  toggleClientPanel(): void {
    this.clientPanelOpen.update(value => !value);
  }

  onClientSelectFromPanel(client: Client): void {
    if (this.posToolbarService.onClientSelect) {
      this.posToolbarService.onClientSelect(client);
    }
    this.clientPanelOpen.set(false);
  }

  onViewModeChange(mode: string): void {
    if (mode === 'carousel' || mode === 'simple' || mode === 'grid') {
      this.posToolbarService.onViewModeChange?.(mode);
    }
  }

  getRoleLabel = getRoleLabel;
}

/** Convert Firestore Timestamp, Date, or date string to a JS Date. */
function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof (value as any).toDate === 'function') return (value as any).toDate();
  if (typeof value === 'string' || typeof value === 'number') return new Date(value);
  return null;
}
